import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch } from "@/app/hooks";
import {
  MAX_CHAT_ATTACHMENTS,
  mergeChatAttachments,
  replaceChatAttachment,
  resourceTypeForFile,
  type ChatAttachment,
} from "@/features/chat/chatAttachments";
import {
  CloudResourceUploadError,
  cloudUploadErrorMessage,
  uploadConversationAttachment,
} from "@/features/chat/cloudResourceUploadClient";
import { baseApi } from "@/shared/api/baseApi";

const MAX_FILE_UPLOAD_CONCURRENCY = 3;

interface UploadJob {
  attachmentScope: string;
  attachmentId: string;
  file: File;
  idempotencyKey: string;
  organizationId: string;
}

interface UploadSource {
  file: File;
  idempotencyKey: string;
}

export function useChatAttachmentUploads(
  organizationId: string,
  attachmentScope: string,
  options: { isUploadDisabled?: boolean } = {},
) {
  const dispatch = useAppDispatch();
  const [attachments, setAttachmentState] = useState<
    ReadonlyArray<ChatAttachment>
  >([]);
  const attachmentsRef = useRef<ReadonlyArray<ChatAttachment>>([]);
  const activeRef = useRef(new Map<string, AbortController>());
  const filesRef = useRef(new Map<string, UploadSource>());
  const mountedRef = useRef(true);
  const attachmentScopeRef = useRef(attachmentScope);
  const organizationIdRef = useRef(organizationId);
  const isUploadDisabledRef = useRef(Boolean(options.isUploadDisabled));
  const pumpRef = useRef<() => void>(() => undefined);
  const queueRef = useRef<UploadJob[]>([]);

  const commitAttachments = useCallback(
    (next: ReadonlyArray<ChatAttachment>) => {
      attachmentsRef.current = next;
      if (mountedRef.current) setAttachmentState(next);
    },
    [],
  );

  const runJob = useCallback(
    async (job: UploadJob) => {
      const controller = new AbortController();
      activeRef.current.set(job.attachmentId, controller);
      try {
        const resourceType = resourceTypeForFile(job.file);
        const completed = await uploadConversationAttachment(
          job.organizationId,
          job.file,
          resourceType,
          {
            idempotencyKey: job.idempotencyKey,
            signal: controller.signal,
          },
        );
        if (
          organizationIdRef.current !== job.organizationId ||
          attachmentScopeRef.current !== job.attachmentScope ||
          !attachmentsRef.current.some(
            (attachment) => attachment.attachmentId === job.attachmentId,
          )
        ) {
          return;
        }
        commitAttachments(
          replaceChatAttachment(
            attachmentsRef.current,
            job.attachmentId,
            {
              attachmentId: completed.resourceId,
              displayName: completed.displayName,
              resourceId: completed.resourceId,
              resourceType: completed.resourceType,
              status: "ready",
            },
          ),
        );
        filesRef.current.delete(job.attachmentId);
        dispatch(
          baseApi.util.invalidateTags([
            { id: job.organizationId, type: "CloudResources" },
            { id: completed.resourceId, type: "CloudResource" },
            { id: job.organizationId, type: "ResourceAvailability" },
          ]),
        );
      } catch (error) {
        if (
          controller.signal.aborted ||
          organizationIdRef.current !== job.organizationId ||
          attachmentScopeRef.current !== job.attachmentScope ||
          !attachmentsRef.current.some(
            (attachment) => attachment.attachmentId === job.attachmentId,
          )
        ) {
          return;
        }
        const current = attachmentsRef.current.find(
          (attachment) => attachment.attachmentId === job.attachmentId,
        );
        if (!current) return;
        if (
          error instanceof CloudResourceUploadError &&
          error.code === "cloud_storage_quota_exceeded"
        ) {
          dispatch(
            baseApi.util.invalidateTags([
              { id: job.organizationId, type: "ResourceAvailability" },
            ]),
          );
        }
        commitAttachments(
          replaceChatAttachment(
            attachmentsRef.current,
            job.attachmentId,
            {
              ...current,
              errorMessage: cloudUploadErrorMessage(error),
              status: "failed",
            },
          ),
        );
      } finally {
        activeRef.current.delete(job.attachmentId);
        pumpRef.current();
      }
    },
    [commitAttachments, dispatch],
  );

  const pumpQueue = useCallback(() => {
    while (
      activeRef.current.size < MAX_FILE_UPLOAD_CONCURRENCY &&
      queueRef.current.length > 0
    ) {
      const job = queueRef.current.shift();
      if (!job) break;
      if (
        job.organizationId !== organizationIdRef.current ||
        job.attachmentScope !== attachmentScopeRef.current ||
        !attachmentsRef.current.some(
          (attachment) => attachment.attachmentId === job.attachmentId,
        )
      ) {
        continue;
      }
      void runJob(job);
    }
  }, [runJob]);

  useEffect(() => {
    pumpRef.current = pumpQueue;
  }, [pumpQueue]);

  const replaceAttachments = useCallback(
    (nextAttachments: ReadonlyArray<ChatAttachment>) => {
      const next = mergeChatAttachments([], nextAttachments);
      const nextIds = new Set(next.map((attachment) => attachment.attachmentId));
      let canceledUpload = false;
      for (const [attachmentId, controller] of activeRef.current) {
        if (!nextIds.has(attachmentId)) {
          canceledUpload = true;
          controller.abort();
        }
      }
      queueRef.current = queueRef.current.filter((job) =>
        nextIds.has(job.attachmentId),
      );
      for (const attachmentId of filesRef.current.keys()) {
        if (!nextIds.has(attachmentId)) filesRef.current.delete(attachmentId);
      }
      commitAttachments(next);
      if (canceledUpload && organizationIdRef.current) {
        dispatch(
          baseApi.util.invalidateTags([
            {
              id: organizationIdRef.current,
              type: "ResourceAvailability",
            },
          ]),
        );
      }
      pumpRef.current();
    },
    [commitAttachments, dispatch],
  );

  const acceptFiles = useCallback(
    (files: ReadonlyArray<File>) => {
      if (!organizationIdRef.current) return "当前没有可用的组织";
      if (isUploadDisabledRef.current) {
        return "云存储已用完，暂时无法上传新文件";
      }
      if (files.length === 0) return "未识别到可上传文件";
      const remaining = Math.max(
        0,
        MAX_CHAT_ATTACHMENTS - attachmentsRef.current.length,
      );
      const nonEmptyFiles = files.filter((file) => file.size > 0);
      const accepted = nonEmptyFiles.slice(0, remaining);
      const candidates: ChatAttachment[] = [];
      const jobs: UploadJob[] = [];

      for (const file of accepted) {
        const attachmentId = `upload:${crypto.randomUUID()}`;
        const idempotencyKey = crypto.randomUUID();
        candidates.push({
          attachmentId,
          displayName: file.name || "未命名文件",
          resourceId: null,
          resourceType: resourceTypeForFile(file),
          status: "uploading",
        });
        filesRef.current.set(attachmentId, { file, idempotencyKey });
        jobs.push({
          attachmentScope: attachmentScopeRef.current,
          attachmentId,
          file,
          idempotencyKey,
          organizationId: organizationIdRef.current,
        });
      }

      commitAttachments(
        mergeChatAttachments(attachmentsRef.current, candidates),
      );
      queueRef.current.push(...jobs);
      pumpRef.current();

      const notices = [
        files.length > nonEmptyFiles.length ? "已忽略空文件" : "",
        nonEmptyFiles.length > accepted.length
          ? remaining === 0
            ? "附件已达上限，单条消息最多 10 个附件"
            : `本次仅接收 ${accepted.length} 个文件，单条消息最多 10 个附件`
          : "",
      ].filter(Boolean);
      return notices.join("；") || null;
    },
    [commitAttachments],
  );

  const retryAttachment = useCallback(
    (attachmentId: string) => {
      if (isUploadDisabledRef.current) return;
      const source = filesRef.current.get(attachmentId);
      const current = attachmentsRef.current.find(
        (attachment) => attachment.attachmentId === attachmentId,
      );
      if (
        !source ||
        !current ||
        current.status !== "failed" ||
        source.file.size <= 0
      ) {
        return;
      }
      commitAttachments(
        replaceChatAttachment(attachmentsRef.current, attachmentId, {
          ...current,
          errorMessage: undefined,
          status: "uploading",
        }),
      );
      const idempotencyKey = crypto.randomUUID();
      filesRef.current.set(attachmentId, {
        file: source.file,
        idempotencyKey,
      });
      queueRef.current.push({
        attachmentScope: attachmentScopeRef.current,
        attachmentId,
        file: source.file,
        idempotencyKey,
        organizationId: organizationIdRef.current,
      });
      pumpRef.current();
    },
    [commitAttachments],
  );

  useEffect(() => {
    isUploadDisabledRef.current = Boolean(options.isUploadDisabled);
  }, [options.isUploadDisabled]);

  useEffect(() => {
    if (
      organizationIdRef.current === organizationId &&
      attachmentScopeRef.current === attachmentScope
    ) {
      return;
    }
    for (const controller of activeRef.current.values()) controller.abort();
    queueRef.current = [];
    filesRef.current.clear();
    organizationIdRef.current = organizationId;
    attachmentScopeRef.current = attachmentScope;
    commitAttachments([]);
  }, [attachmentScope, commitAttachments, organizationId]);

  useEffect(() => {
    const activeUploads = activeRef.current;
    const uploadFiles = filesRef.current;
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      for (const controller of activeUploads.values()) controller.abort();
      queueRef.current = [];
      uploadFiles.clear();
    };
  }, []);

  return {
    acceptFiles,
    attachments,
    replaceAttachments,
    retryAttachment,
  };
}
